// src/redux/slices/authSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';

/**
 * loginUser accepts { username, password, source }.
 * It will persist authToken and also return source in fulfilled payload.
 */
export const loginUser = createAsyncThunk(
  'auth/loginUser',
  async ({ username, password, source }, { rejectWithValue }) => {
    try {
      const resp = await axios.post(
        `${process.env.REACT_APP_IAM_BASE_URL}${process.env.REACT_APP_LOGIN_ENDPOINT}`,
        { username, password }
      );
      const { auth_token: token } = resp.data;
      const decoded = jwtDecode(token);

      const { parentClientId, merchantId, exp } = decoded;
      if (!parentClientId || !merchantId) {
        throw new Error('Invalid token payload');
      }
      if (exp * 1000 < Date.now()) {
        throw new Error('Token already expired');
      }

      // Persist to localStorage
      localStorage.setItem('authToken', token);
      localStorage.setItem('parentClientId', parentClientId);
      localStorage.setItem('merchantId', merchantId);

      // Keep source in localStorage too (so direct visits and other tabs see it)
      if (source) {
        localStorage.setItem('source', source);
      } else {
        // if not provided, keep what's already in localStorage or default to wayuvega
        const existing = localStorage.getItem('source');
        if (!existing) localStorage.setItem('source', 'wayuvega');
      }

      return { token, parentClientId, merchantId, exp, source: source || localStorage.getItem('source') || 'wayuvega' };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  }
);

const initialState = {
  isAuthenticated: false,
  token: null,
  parentClientId: null,
  merchantId: null,
  userExp: null,     // Unix timestamp (in seconds) when token expires
  loading: false,
  error: null,
  initialized: false,
  // track which branded login was used. initialize from localStorage if present.
  source: localStorage.getItem('source') || 'wayuvega',
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout(state) {
      state.isAuthenticated = false;
      state.token = null;
      state.parentClientId = null;
      state.merchantId = null;
      state.userExp = null;
      state.error = null;
      state.initialized = true;
      // Clear localStorage auth keys but preserve source
      localStorage.removeItem('authToken');
      localStorage.removeItem('parentClientId');
      localStorage.removeItem('merchantId');
      // DO NOT remove localStorage 'source' here — keep it so logout redirects to the right login
    },
    restoreSession(state, action) {
      const { token, user } = action.payload;
      
      // Extract fields from decoded token
      const parentClientId = user.parentClientId || user.clientId;
      const merchantId = user.merchantId;
      const exp = user.exp;
      const source = user.source || localStorage.getItem('source') || 'wayuvega';
      
      // Update Redux state
      state.isAuthenticated = true;
      state.token = token;
      state.parentClientId = parentClientId;
      state.merchantId = merchantId;
      state.userExp = exp;
      state.error = null;
      state.initialized = true;
      state.source = source;
      
      // 🔥 PERSIST TO LOCALSTORAGE
      try {
        localStorage.setItem('authToken', token);
        if (parentClientId) localStorage.setItem('parentClientId', parentClientId);
        if (merchantId) localStorage.setItem('merchantId', merchantId);
        localStorage.setItem('source', source);
      } catch (e) {
        console.error('Failed to save to localStorage:', e);
      }
    },
    markInitialized(state) { state.initialized = true; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        const { token, parentClientId, merchantId, exp, source } = action.payload;
        state.loading = false;
        state.isAuthenticated = true;
        state.token = token;
        state.parentClientId = parentClientId;
        state.merchantId = merchantId;
        state.userExp = exp;
        state.error = null;
        if (source) {
          state.source = source;
          try { localStorage.setItem('source', source); } catch (e) {}
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Login failed';
      });
  },
});

export const { logout, restoreSession, markInitialized } = authSlice.actions;
export default authSlice.reducer;
